import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  SmartToy as BotIcon,
  Security as SecurityIcon,
  IntegrationInstructions as IntegrationIcon,
  Translate as LanguageIcon,
  Group as GroupIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  Chat as ChatIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';

import BotIconImage from "../../images/BotIcon.png";
import Footer from "../../components/footer/Footer";

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#6366F1',
      light: '#818CF8',
      dark: '#4F46E5',
    },
    secondary: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    background: {
      default: '#F9FAFB',
    },
  },
  typography: {
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
  },
});

function Bot() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <IntegrationIcon sx={{ fontSize: 40 }} />,
      title: 'Multi-Platform Integration',
      description: 'Connect with all your favorite messaging platforms in one place.'
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 40 }} />,
      title: 'Multi-Language Support',
      description: 'Communicate with users in over 100 languages seamlessly.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Advanced Security',
      description: 'Enterprise-grade security to keep your data safe and compliant.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Lightning Fast',
      description: 'Quick responses with our optimized AI algorithms.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      title: 'Scalable for Teams',
      description: 'Perfect for individuals, teams, and enterprises of any size.'
    },
    {
      icon: <BotIcon sx={{ fontSize: 40 }} />,
      title: 'Smart AI',
      description: 'Powered by cutting-edge artificial intelligence for natural conversations.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'CTO at TechCorp',
      content: 'Unified Chat Bot has transformed our customer service. Response times decreased by 70% and customer satisfaction skyrocketed.',
      rating: 5
    },
    {
      name: 'Michael Chen',
      role: 'Product Manager at StartupCo',
      content: 'The multi-platform integration saved us countless hours. Now we manage all customer communications from one dashboard.',
      rating: 5
    },
    {
      name: 'Emily Rodriguez',
      role: 'E-commerce Owner',
      content: 'The AI understands context so well that customers often don\'t realize they\'re talking to a bot. Absolutely incredible!',
      rating: 4
    }
  ];

  const pricingPlans = [
    {
      title: 'Starter',
      price: '$29',
      period: 'per month',
      features: ['Up to 500 conversations', '2 platform integrations', 'Basic analytics', 'Email support'],
      highlighted: false
    },
    {
      title: 'Professional',
      price: '$79',
      period: 'per month',
      features: ['Unlimited conversations', '5 platform integrations', 'Advanced analytics', 'Priority support', 'Custom branding'],
      highlighted: true
    },
    {
      title: 'Enterprise',
      price: 'Custom',
      period: '',
      features: ['Unlimited everything', 'All platform integrations', 'Dedicated account manager', 'SLA guarantee', 'Custom AI training'],
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to set up Unified Chat Bot?',
      answer: 'Most customers can get up and running in less than an hour. Our intuitive setup wizard guides you through the process step by step.'
    },
    {
      question: 'Can I use it on multiple websites?',
      answer: 'Yes! Your subscription allows you to use Unified Chat Bot on all your properties. Higher tiers offer additional team members and collaboration features.'
    },
    {
      question: 'What languages does the bot support?',
      answer: 'Our bot currently supports over 100 languages with native-level fluency in the most popular ones. We\'re constantly adding more language support.'
    },
    {
      question: 'How does the AI handle complex queries?',
      answer: 'Our AI uses a combination of natural language processing and machine learning to understand context and intent. For particularly complex issues, it can seamlessly escalate to a human agent.'
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Hero Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #feffff 0%, #feffff 100%)',
          color: 'white',
          py: 10,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" component="h1" gutterBottom>
                Unified AI Agent
              </Typography>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                One bot to rule all your messaging platforms. AI-powered conversations across WhatsApp, Messenger, Telegram, and more.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main',
                    transition:'.3s',
                    '&:hover': {
                      bgcolor: 'grey.100',
                      scale:'1.1'
                    }
                  }}
                >
                  Get Started Free
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    borderColor: 'white', 
                    color: 'white',
                    bgcolor:'#020842',
                    transition:'.3s',
                    '&:hover': {
                      borderColor: 'grey.300',
                      bgcolor: '#020842',
                      scale:'1.1'
                    }
                  }}
                >
                  Watch Demo
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <img src={BotIconImage} style={{height:"450px"}} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ 
                py: 10 ,
                background: 'linear-gradient(135deg, #fff2da 0%, #e2f0ff 100%)',
            }}
      
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" textAlign="center" gutterBottom>
            Powerful Features
          </Typography>
          <Typography variant="h6" color="textSecondary" textAlign="center" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
            Everything you need to provide exceptional customer service across all messaging platforms
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                  variant="outlined"
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      {/* <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" textAlign="center" gutterBottom>
            What Our Customers Say
          </Typography>
          <Typography variant="h6" color="textSecondary" textAlign="center" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
            Join thousands of satisfied users who have transformed their customer communication
          </Typography>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon 
                        key={i} 
                        sx={{ 
                          color: i < testimonial.rating ? 'warning.main' : 'grey.300',
                          fontSize: 20
                        }} 
                      />
                    ))}
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "{testimonial.content}"
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {testimonial.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {testimonial.role}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* Pricing Section */}
      {/* <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" textAlign="center" gutterBottom>
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h6" color="textSecondary" textAlign="center" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
            No hidden fees. No contracts. Choose the plan that works best for you.
          </Typography>
          
          <Grid container spacing={3} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: plan.highlighted ? `2px solid ${theme.palette.primary.main}` : undefined,
                    transform: plan.highlighted ? 'scale(1.03)' : undefined,
                    position: 'relative'
                  }}
                  variant={plan.highlighted ? 'elevation' : 'outlined'}
                  elevation={plan.highlighted ? 4 : 0}
                >
                  {plan.highlighted && (
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: 'primary.main',
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '0 0 4px 4px',
                        fontSize: '0.875rem'
                      }}
                    >
                      Most Popular
                    </Box>
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" component="h3" gutterBottom textAlign="center">
                      {plan.title}
                    </Typography>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="h3" component="div" fontWeight="bold">
                        {plan.price}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {plan.period}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      {plan.features.map((feature, i) => (
                        <ListItem key={i}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <CheckIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                    <Button 
                      fullWidth 
                      variant={plan.highlighted ? "contained" : "outlined"}
                      size="large"
                      sx={{ mt: 3 }}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box> */}

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h2" component="h2" textAlign="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" color="textSecondary" textAlign="center" sx={{ mb: 6 }}>
            Everything you need to know about the product
          </Typography>
          
          {faqs.map((faq, index) => (
            <Accordion key={index} elevation={2}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" component="h4">
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="textSecondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, background: 'linear-gradient(135deg, #6366F1 0%, #000ba1 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" color='white' gutterBottom>
            Ready to transform your customer communication?
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ mb: 4, opacity: 0.9, color:'white' }}>
            Join thousands of businesses using Unified Chat Bot to provide exceptional customer service.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button 
              variant="contained" 
              size="large" 
              sx={{ 
                bgcolor: 'white', 
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Get Started Free
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </ThemeProvider>
  );
}

export default Bot;

// const Bot = () => (
//   <Box
//     sx={{
//       height: '100vh',
//       display: 'flex',
//       flexDirection: 'column',
//       justifyContent: 'center',
//       alignItems: 'center',
//       background: 'linear-gradient(to right, #6a11cb, #2575fc)',
//       color: '#fff',
//       textAlign: 'center',
//       px: 3,
//     }}
//   >
//     <Typography variant="h2" gutterBottom>
//       Meet Unified Bot
//     </Typography>
//     <Typography variant="h6" sx={{ maxWidth: 600 }}>
//       Your all-in-one AI assistant for smarter conversations, automation, and productivity.
//     </Typography>
//     <Button variant="contained" color="secondary" sx={{ mt: 4 }}>
//       Get Started
//     </Button>
//   </Box>
// );

// export default Bot;